/**
 * Premium inline slideshow for the product page gallery.
 * Replaces fullscreen zoom with arrows, thumbnails, counter, and tap-to-advance.
 */
class TccPdpSlideshow {
  /** @type {HTMLElement | null} */
  #root = null;

  /** @type {import('./media-gallery').MediaGallery | null} */
  #gallery = null;

  /** @type {HTMLElement | null} */
  #counter = null;

  /** @type {((e: KeyboardEvent) => void) | null} */
  #onKeydown = null;

  /** @type {((e: Event) => void) | null} */
  #onSlideChange = null;

  /** @type {((e: PointerEvent) => void) | null} */
  #onPointerDown = null;

  /** @type {((e: PointerEvent) => void) | null} */
  #onPointerUp = null;

  /** @type {{ x: number; y: number } | null} */
  #pointerStart = null;

  constructor() {
    this.#root = document.querySelector('.tcc-pdp-gallery');
    if (!this.#root) return;

    const gallery = this.#root.querySelector('media-gallery');
    if (!(gallery instanceof HTMLElement)) return;

    this.#gallery = /** @type {import('./media-gallery').MediaGallery} */ (gallery);
    this.#init();
  }

  #init() {
    const slideshow = this.#getSlideshow();
    if (!slideshow) return;

    slideshow.classList.add('tcc-pdp-slideshow');

    const slideCount = slideshow.refs?.slides?.length ?? 0;
    if (slideCount <= 1) return;

    this.#mountCounter(slideshow);
    this.#bindSlideSync(slideshow);
    this.#bindTapNavigation(slideshow);
    this.#bindKeyboard(slideshow);
    this.#updateCounter(slideshow);
  }

  /** @returns {import('./slideshow').Slideshow | null} */
  #getSlideshow() {
    return this.#gallery?.slideshow ?? this.#gallery?.refs?.slideshow ?? null;
  }

  /**
   * @param {import('./slideshow').Slideshow} slideshow
   */
  #mountCounter(slideshow) {
    const container = slideshow.querySelector('slideshow-container');
    if (!container) return;

    const total = slideshow.refs?.slides?.length ?? 0;
    const counter = document.createElement('div');
    counter.className = 'tcc-pdp-slideshow__counter';
    counter.setAttribute('aria-live', 'polite');
    counter.innerHTML = `
      <span class="tcc-pdp-slideshow__counter-current">1</span>
      <span class="tcc-pdp-slideshow__counter-sep" aria-hidden="true">/</span>
      <span class="tcc-pdp-slideshow__counter-total">${total}</span>
    `;
    container.appendChild(counter);
    this.#counter = counter;
  }

  /**
   * @param {import('./slideshow').Slideshow} slideshow
   */
  #bindSlideSync(slideshow) {
    this.#onSlideChange = () => this.#updateCounter(slideshow);
    slideshow.addEventListener('slideshow:select', this.#onSlideChange);
  }

  /**
   * @param {import('./slideshow').Slideshow} slideshow
   */
  #bindTapNavigation(slideshow) {
    const scroller = slideshow.querySelector('slideshow-slides');
    if (!scroller) return;

    scroller.setAttribute('tabindex', '0');
    scroller.setAttribute('role', 'region');
    scroller.setAttribute('aria-label', 'Product images');

    this.#onPointerDown = (event) => {
      this.#pointerStart = { x: event.clientX, y: event.clientY };
    };

    this.#onPointerUp = (event) => {
      if (!this.#pointerStart) return;

      const dx = Math.abs(event.clientX - this.#pointerStart.x);
      const dy = Math.abs(event.clientY - this.#pointerStart.y);
      this.#pointerStart = null;

      if (dx > 12 || dy > 12) return;

      const target = event.target;
      if (target instanceof Element && target.closest('button, a, slideshow-controls, .slideshow-controls__thumbnail')) {
        return;
      }

      const rect = scroller.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const ratio = x / rect.width;

      if (ratio < 0.28) {
        slideshow.previous(event);
      } else if (ratio > 0.72) {
        slideshow.next(event);
      }
    };

    scroller.addEventListener('pointerdown', this.#onPointerDown);
    scroller.addEventListener('pointerup', this.#onPointerUp);
  }

  /**
   * @param {import('./slideshow').Slideshow} slideshow
   */
  #bindKeyboard(slideshow) {
    const scroller = slideshow.querySelector('slideshow-slides');
    if (!scroller) return;

    this.#onKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        slideshow.previous(event);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        slideshow.next(event);
      }
    };

    scroller.addEventListener('keydown', this.#onKeydown);
  }

  /**
   * @param {import('./slideshow').Slideshow} slideshow
   */
  #updateCounter(slideshow) {
    if (!this.#counter) return;

    const current = slideshow.querySelector('.tcc-pdp-slideshow__counter-current');
    if (!current) return;

    const index = typeof slideshow.current === 'number' ? slideshow.current + 1 : 1;
    current.textContent = String(index);
  }

  disconnect() {
    const slideshow = this.#getSlideshow();

    if (slideshow && this.#onSlideChange) {
      slideshow.removeEventListener('slideshow:select', this.#onSlideChange);
    }

    const scroller = slideshow?.querySelector('slideshow-slides');
    if (scroller) {
      if (this.#onPointerDown) scroller.removeEventListener('pointerdown', this.#onPointerDown);
      if (this.#onPointerUp) scroller.removeEventListener('pointerup', this.#onPointerUp);
      if (this.#onKeydown) scroller.removeEventListener('keydown', this.#onKeydown);
    }

    this.#counter?.remove();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new TccPdpSlideshow());
} else {
  new TccPdpSlideshow();
}
